"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { getCategoriesHirarchy } from "../../../services/getAttributes.service";
import { Button } from "src/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import { getLocalizedText } from "src/utils/getLocalizedText";
import { useSelectedFilters } from "src/store/useSelectedFilters";
import { Category } from "src/interfaces/product.interface";
import './navbar.css';


export default function CategoryNavLinks() {
  const router = useRouter();
  const [categories, setCategories] = useState<Category[]>([]);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const {
    setSelectedPrice,
    setSelectedBrands,
    setSelectedSize,
    setSelectedConditions,
    setSelectedColors,
    setSelectedCategories,
    setSelectedSubCategories1,
    setSelectedSubCategories2,
    setSelectedSubCategories3,
  } = useSelectedFilters();

  const fetchCategories = async () => {
    const categories = await getCategoriesHirarchy();

    // Recursive function to filter out "All" categories
    const removeAllCategories = (categoriesArray: Category[]): Category[] => {
      return categoriesArray
        .filter((category) => category?.title_en?.toLowerCase() !== "all")
        .map((category) => ({
          ...category,
          subCategories1: category.subCategories1
            ? removeAllCategories(category.subCategories1)
            : [],
        }));
    };

    const filteredCategories = removeAllCategories(categories);
    setCategories(filteredCategories);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCategoryClick = (
    id: number,
    level: number,
    parentId?: number,
    grandParentId?: number,
  ) => {
    switch (level) {
      case 0:
        setSelectedCategories((prevSelected) =>
          prevSelected.includes(id) ? prevSelected : [id],
        );
        setSelectedSubCategories1([]);
        setSelectedSubCategories2([]);
        setSelectedSubCategories3([]);
        setSelectedPrice("");
        setSelectedBrands([]);
        setSelectedSize([]);
        setSelectedConditions([]);
        setSelectedColors([]);
        break;

      case 1:
        setSelectedSubCategories1((prevSelected) =>
          prevSelected.includes(id) ? prevSelected : [id],
        );
        if (parentId) {
          setSelectedCategories((prevSelected) =>
            prevSelected.includes(parentId) ? prevSelected : [parentId],
          );
        }
        setSelectedSubCategories2([]);
        setSelectedSubCategories3([]);
        setSelectedPrice("");
        setSelectedBrands([]);
        setSelectedSize([]);
        setSelectedConditions([]);
        setSelectedColors([]);
        break;

      case 2:
        setSelectedSubCategories2((prevSelected) =>
          prevSelected.includes(id) ? prevSelected : [id],
        );
        if (parentId) {
          setSelectedSubCategories1((prevSelected) =>
            prevSelected.includes(parentId) ? prevSelected : [parentId],
          );
        }
        if (grandParentId) {
          setSelectedCategories((prevSelected) =>
            prevSelected.includes(grandParentId)
              ? prevSelected
              : [grandParentId],
          );
        }
        setSelectedSubCategories3([]);
        setSelectedPrice("");
        setSelectedBrands([]);
        setSelectedSize([]);
        setSelectedConditions([]);
        setSelectedColors([]);
        break;

      case 3:
        setSelectedSubCategories3((prevSelected) =>
          prevSelected.includes(id) ? prevSelected : [id],
        );
        if (parentId) {
          setSelectedSubCategories2((prevSelected) =>
            prevSelected.includes(parentId) ? prevSelected : [parentId],
          );
        }
        if (grandParentId) {
          setSelectedSubCategories1((prevSelected) =>
            prevSelected.includes(grandParentId)
              ? prevSelected
              : [grandParentId],
          );
        }
        break;

      default:
        break;
    }
  };

  return (
    <div className="items-center justify-center gap-x-5 overflow-x-auto text-sm text-black dark:text-white dark:text-opacity-50 md:flex">
      <div className="seconday_menu text-center rounded overflow-hidden flex border border-[#e4e4e4]">
      {isLoading
        ? Array.from({ length: 9 }).map((_, index) => (
          <div
            key={index}
            className="h-10 w-32 animate-pulse rounded-xl bg-secondary dark:bg-tertiary mx-2"
          />
        ))
        : categories?.map((category: any, i: number) => {
          const hasSub1 = category?.subCategories1?.length > 0;

          return hasSub1 ? (
            <DropdownMenu
              key={i}
              open={openIndex === i}
              onOpenChange={(isOpen) => setOpenIndex(isOpen ? i : null)}
            >
              <DropdownMenuTrigger asChild className="hover:bg-transparent">
                <Button
                  variant="outline"
                  onMouseEnter={() => {
                    setOpenIndex(i);
                  }}
                  onMouseLeave={() => setOpenIndex(null)}
                  onClick={() => {
                    handleCategoryClick(category?.id, 0);
                    router.push(
                      `/categories/${category.id}?category_level=${1}&category_name=${encodeURIComponent(getLocalizedText(category, "title"))}`,
                    );
                  }}
                >
                  {getLocalizedText(category, "title")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="rounded-none"
                onMouseEnter={() => setOpenIndex(i)}
                onMouseLeave={() => setOpenIndex(null)}
              >
                {/* Subcategories Level 1 */}
                {hasSub1 && (
                  <DropdownMenuGroup>
                    {category?.subCategories1?.map((sub1: any, j: number) => {
                      const hasSub2 = sub1?.subCategories2?.length > 0;

                      return hasSub2 ? (
                        <DropdownMenuSub key={j}>
                          <DropdownMenuSubTrigger
                            className="my-1 min-w-[180px] text-sm hover:text-primary hover:underline"
                            onClick={() => {
                              handleCategoryClick(
                                sub1?.id,
                                1,
                                // Parent ID
                                category?.id,
                              );
                              router.push(
                                `/categories/${sub1.id}?category_level=${2}&category_name=${encodeURIComponent(getLocalizedText(sub1, "title"))}`,
                              );
                            }}
                          >
                            {getLocalizedText(sub1, "title")}
                          </DropdownMenuSubTrigger>
                          <DropdownMenuPortal>
                            <DropdownMenuSubContent className="-mt-[8.5px] ml-[5px] rounded-none">
                              {/* Subcategories Level 2 */}
                              {sub1?.subCategories2?.map(
                                (sub2: any, k: number) => {
                                  const hasSub3 =
                                    sub2?.subCategories3?.length > 0;

                                  return hasSub3 ? (
                                    <DropdownMenuSub key={k}>
                                      <DropdownMenuSubTrigger
                                        className="my-1 min-w-[180px]  hover:text-primary hover:underline"
                                        onClick={() => {
                                          handleCategoryClick(
                                            sub2?.id,
                                            2,
                                            sub1?.id,
                                            // Grandparent ID
                                            category?.id,
                                          );
                                          router.push(
                                            `/categories/${sub2.id}?category_level=${3}&category_name=${encodeURIComponent(getLocalizedText(sub2, "title"))}`,
                                          );
                                        }}
                                      >
                                        {getLocalizedText(sub2, "title")}
                                      </DropdownMenuSubTrigger>
                                      {/* <DropdownMenuPortal> 
                                          <DropdownMenuSubContent className="-mt-[5px] ml-[6px] rounded-none">
                                            // Subcategories Level 3 
                                            {sub2?.subCategories3?.map(
                                              (sub3: any, l: number) => (
                                                <DropdownMenuItem
                                                  key={l}
                                                  className="my-1 min-w-[180px] text-sm hover:font-bold hover:text-primary hover:underline focus:text-primary"
                                                  onClick={() => {
                                                    router.prefetch(
                                                      `/categories/${sub3.id}?category_level=${4}&category_name=${encodeURIComponent(getLocalizedText(sub3, "title"))}`,
                                                    );
                                                    router.push(
                                                      `/categories/${sub3.id}?category_level=${4}&category_name=${encodeURIComponent(getLocalizedText(sub3, "title"))}`,
                                                    );
                                                  }}
                                                >
                                                  {getLocalizedText(
                                                    sub3,
                                                    "title",
                                                  )}
                                                </DropdownMenuItem>
                                              ),
                                            )}
                                          </DropdownMenuSubContent>
                                        </DropdownMenuPortal> */}
                                    </DropdownMenuSub>
                                  ) : (
                                    <DropdownMenuItem
                                      key={k}
                                      className="my-1 min-w-[180px] text-sm  hover:underline focus:text-primary"
                                      onClick={() => {
                                        handleCategoryClick(
                                          sub2?.id,
                                          2,
                                          sub1?.id,
                                          // Grandparent ID
                                          category?.id,
                                        );
                                        router.push(
                                          `/categories/${sub2.id}?category_level=${3}&category_name=${encodeURIComponent(getLocalizedText(sub2, "title"))}`,
                                        );
                                      }}
                                    >
                                      {getLocalizedText(sub2, "title")}
                                    </DropdownMenuItem>
                                  );
                                },
                              )}
                            </DropdownMenuSubContent>
                          </DropdownMenuPortal>
                        </DropdownMenuSub>
                      ) : (
                        <DropdownMenuItem
                          key={j}
                          className="my-1 min-w-[180px] text-sm  hover:underline focus:text-primary"
                          onClick={() => {
                            handleCategoryClick(category?.id, 0);
                            router.push(
                              `/categories/${sub1.id}?category_level=${1}&category_name=${encodeURIComponent(getLocalizedText(sub1, "title"))}`,
                            );
                          }}
                        >
                          {getLocalizedText(sub1, "title")}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuGroup>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              key={i}
              variant="outline"
              onMouseEnter={() => {
                router.prefetch(
                  `/categories/${category.id}?category_level=${1}&category_name=${encodeURIComponent(getLocalizedText(category, "title"))}`,
                );
              }}
              onClick={() => {
                handleCategoryClick(category?.id, 0);
                router.push(
                  `/categories/${category.id}?category_level=${1}&category_name=${encodeURIComponent(getLocalizedText(category, "title"))}`,
                );
              }}
            >
              {getLocalizedText(category, "title")}
            </Button>
          );
        })}
        </div>
    </div>
  );
}
